import { parseISO, differenceInDays, parse, isAfter, format, addDays, subDays, differenceInHours } from 'date-fns'
import { DatePickerOptions } from 'element-ui/types/date-picker'
/**
 * 未来の時間選択は設定できません
 *
 * @readonly
 * @constant
 */
export const pickerOptions: DatePickerOptions = {
  /**
   * タイムセレクタをクリックしたときの処理
   *
   * @param {Date} time 現在の日付
   * @returns {boolean} 未来の日は選択できません
   */
  disabledDate: (time: Date): boolean => {
    if (time.getTime() > Date.now()) {
      return true
    }
    return false
  }
}

/**
 * 過去の時間選択は設定できません
 *
 * @readonly
 * @constant
 */
export const pickerOptionsPast: DatePickerOptions = {
  /**
   * タイムセレクタをクリックしたときの処理
   *
   * @param {Date} time 現在の日付
   * @returns {boolean} 過去の日は選択できません
   */
  disabledDate: (time: Date): boolean => {
    const now = new Date()
    // Disable dates before today
    return time.getTime() < now.setHours(0, 0, 0, 0)
  }
}

/**
 * 期間を取得する
 *
 * @param {string} date 時間
 * @returns {number | null} 期間数
 */
export function getIntervalTime(date: string): number | null {
  if (!date) {
    return null
  }
  date = date.replaceAll('/', '-')
  const dateObject = parseISO(date)
  const days = differenceInDays(dateObject, new Date())
  return days + 1
}

/**
 * 文字列を時間に変換する
 *
 * @param {string} date 時間
 * @param {string} pattern パターン
 * @returns {number} 期間数
 */
export function parseDate(date: string, pattern = 'yyyy/MM/dd'): Date {
  return parse(date, pattern, new Date())
}

/**
 * 時間対比
 *
 * @param {string} date 時間
 * @param {string} dateToCompare 時間
 * @param {string} pattern パターン
 * @returns {boolean} 期間数
 */
export function isDateAfter(date: string, dateToCompare: string, pattern = 'yyyy/MM/dd'): boolean {
  const first = parseDate(date, pattern)
  const last = parseDate(dateToCompare, pattern)
  return isAfter(first, last)
}

/**
 * システムの現在の時刻を取得する
 *
 * @param {string} pattern パターン
 * @returns {string} システムの現在の時刻
 */
export function getCurrentDate(pattern = 'yyyy/MM/dd'): string {
  return format(new Date(), pattern)
}

/**
 * 翌日を取得する
 *
 * @param {string} date 時間
 * @param {string} pattern 時間
 * @returns {string} 翌日
 */
export function getNextDay(date: string, pattern = 'yyyy/MM/dd'): string {
  const formatDate = parse(date, pattern, new Date())
  const nextDay = addDays(formatDate, 1)
  return format(nextDay, 'yyyy/MM/dd')
}

/**
 * 前日を取得する
 *
 * @param {string} date 時間
 * @param {string} pattern 時間
 * @returns {string} 前日
 */
export function getPreviousDay(date: string, pattern = 'yyyy/MM/dd'): string {
  const formatDate = parse(date, pattern, new Date())
  const previousDay = subDays(formatDate, 1)
  return format(previousDay, 'yyyy/MM/dd')
}

/**
 * 時間間隔が特定日を超えているかどうかを確認
 *
 * @param {string} date 時間
 * @param {string} dateToCompare 時間
 * @param {number} day 天数
 * @returns {boolean} 指定日数を超えているか
 */
export function isDateOver(date: string, dateToCompare: string, day: number): boolean {
  const end = parse(date, 'yyyy/MM/dd HH:mm:ss', new Date())
  const start = parse(dateToCompare, 'yyyy/MM/dd HH:mm:ss', new Date())
  const days = differenceInDays(end, start)

  if (days >= day) {
    return true
  } else {
    return false
  }
}

/**
 * 時間間隔を取得する
 *
 * @param {string} targetDate 指定時間
 * @returns {number} hours 時間間隔
 */
export function getTimeInterval(targetDate: string): number {
  const start = parse(targetDate, 'yyyy/MM/dd HH:mm', new Date())
  const end = new Date()

  return differenceInHours(end, start)
}

/**
 * 日にち間隔が特定日を超えているかどうかを確認
 *
 * @param {string} date 時間
 * @param {string} dateToCompare 時間
 * @param {number} day 天数
 * @returns {boolean} 指定日数を超えているか
 */
export function isDateDayOver(date: string, dateToCompare: string, day: number): boolean {
  const end = parseDate(date)
  const start = parseDate(dateToCompare)
  const days = differenceInDays(end, start)

  if (days >= day) {
    return true
  } else {
    return false
  }
}

/**
 * 日付を正しい形式で入力
 *
 * @param {string} date 時間
 * @param {string} pattern 形式
 * @returns {boolean} 日付を正しい形式で入力
 */
export function correctTimeFormat(date: string, pattern = 'yyyy/MM/dd'): boolean {
  return 'Invalid Date' !== parse(date, pattern, new Date()).toString()
}
